import React from "react";
import {
  INLINE_CODE_GUARD_STYLE,
  SCROLL_CONTAINER_GUARD_STYLE,
  mergeGuardStyle,
} from "../utils/width-guard";

export interface CodeBlockProps extends React.HTMLAttributes<HTMLElement> {
  children?: React.ReactNode;
  className?: string;
}

/**
 * Code block component with language detection.
 *
 * `<pre>` defaults to `white-space: pre`, so a single long line has an unbounded
 * min-content width and would otherwise stretch the article column past the
 * viewport on a phone. The block scrolls horizontally inside its own box instead.
 */
export function CodeBlock({
  children,
  className,
  style,
  ...props
}: CodeBlockProps) {
  // Extract language from className (e.g., "language-javascript")
  const language = React.useMemo(() => {
    if (!className) return null;
    const match = className.match(/language-(\w+)/);
    return match ? match[1] : null;
  }, [className]);

  return (
    <pre
      className={className}
      data-language={language}
      style={mergeGuardStyle(SCROLL_CONTAINER_GUARD_STYLE, style)}
      {...props}
    >
      <code>{children}</code>
    </pre>
  );
}

/**
 * Inline code component.
 *
 * Long inline tokens (CLI invocations, identifiers, URLs) are allowed to break
 * anywhere so they cannot push the surrounding text block wider than its column.
 */
export function InlineCode({ children, style, ...props }: CodeBlockProps) {
  return (
    <code style={mergeGuardStyle(INLINE_CODE_GUARD_STYLE, style)} {...props}>
      {children}
    </code>
  );
}
